import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Story from './components/Story';
import Services from './components/Services';
import Products from './components/Products';
import Comparison from './components/Comparison';
import Process from './components/Process';
import Mission from './components/Mission';
import Testimonials from './components/Testimonials';
import BlogPreview from './components/BlogPreview';
import CTA from './components/CTA';
import Footer from './components/Footer';
import BlogList from './components/BlogList';
import BlogPostView from './components/BlogPost';
import ContactPage from './components/ContactPage';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import WhatsAppWidget from './components/WhatsAppWidget';
import ResourcesPage from './components/ResourcesPage';
import ProductsPage from './components/ProductsPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import SEO from './components/SEO';
import WhiteLabelSupplements from './components/WhiteLabelSupplements';
import PrivateLabelSupplements from './components/PrivateLabelSupplements';
import SupplementsForInfluencers from './components/SupplementsForInfluencers';
import AboutUs from './components/AboutUs';
import Certifications from './components/Certifications';
import { blogPosts as initialBlogPosts } from './data/blogData';
import { BlogPost } from './types';

type PageView = 'home' | 'blog' | 'post' | 'contact' | 'admin-login' | 'admin-dashboard' | 'resources' | 'products' | 'privacy' | 'white-label' | 'private-label' | 'influencers' | 'about' | 'certifications';

function App() {
  const getInitialView = (): PageView => {
    const path = window.location.pathname;
    if (path === '/blog') return 'blog';
    if (path === '/contact') return 'contact';
    if (path === '/resources') return 'resources';
    if (path === '/products') return 'products';
    if (path === '/privacy') return 'privacy';
    if (path === '/white-label-supplements') return 'white-label';
    if (path === '/private-label-supplements') return 'private-label';
    if (path === '/supplements-for-influencers') return 'influencers';
    if (path === '/about') return 'about';
    if (path === '/certifications') return 'certifications';
    if (path === '/admin') return 'admin-login';
    // If it's a blog post, could handle /post/... but we'll stick to 'home' fallback for simplicity
    return 'home';
  };

  const [currentView, setCurrentView] = useState<PageView>(getInitialView());
  const [activePost, setActivePost] = useState<BlogPost | null>(null);
  
  // Handle back/forward browser buttons
  useEffect(() => {
    const handlePopState = () => {
      setCurrentView(getInitialView());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // State to hold blog posts (both seed and custom)
  const [posts, setPosts] = useState<BlogPost[]>(initialBlogPosts);

  // Load posts on mount
  useEffect(() => {
    const savedPosts = localStorage.getItem('customBlogPosts');
    if (savedPosts) {
        try {
            const parsedCustomPosts = JSON.parse(savedPosts);
            setPosts([...parsedCustomPosts, ...initialBlogPosts]);
        } catch (e) {
            setPosts(initialBlogPosts);
        }
    } else {
        setPosts(initialBlogPosts);
    }
  }, []);

  // Function to handle navigation
  const handleNavigate = (view: PageView, sectionId?: string) => {
    setCurrentView(view);
    
    // Update URL
    let newPath = '/';
    if (view === 'blog') newPath = '/blog';
    if (view === 'contact') newPath = '/contact';
    if (view === 'resources') newPath = '/resources';
    if (view === 'products') newPath = '/products';
    if (view === 'privacy') newPath = '/privacy';
    if (view === 'white-label') newPath = '/white-label-supplements';
    if (view === 'private-label') newPath = '/private-label-supplements';
    if (view === 'influencers') newPath = '/supplements-for-influencers';
    if (view === 'about') newPath = '/about';
    if (view === 'certifications') newPath = '/certifications';
    if (view === 'admin-login') newPath = '/admin';

    if (window.location.pathname !== newPath) {
        window.history.pushState(null, '', newPath);
    }
    
    if (view === 'resources' && sectionId) {
        window.scrollTo({ top: 0, behavior: 'instant' }); // Reset first
        setTimeout(() => {
            const element = document.getElementById(sectionId);
            if (element) {
                const headerOffset = 100;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth"
                });
            }
        }, 100);
        return;
    }

    // If switching to home with a section ID
    if (view === 'home') {
      if (sectionId) {
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleReadPost = (post: BlogPost) => {
    setActivePost(post);
    setCurrentView('post');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToBlog = () => {
    setActivePost(null);
    setCurrentView('blog');
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  };

  // Admin Functions
  const handleAdminLoginSuccess = () => {
    setCurrentView('admin-dashboard');
  };

  const handleAddPost = (newPost: BlogPost) => {
    // Add to state
    const updatedPosts = [newPost, ...posts];
    setPosts(updatedPosts);
    
    // Save ONLY custom posts to local storage to avoid duplicating seed data forever
    // Filter out seed IDs
    const seedIds = initialBlogPosts.map(p => p.id);
    const customPosts = updatedPosts.filter(p => !seedIds.includes(p.id));
    localStorage.setItem('customBlogPosts', JSON.stringify(customPosts));
  };

  const handleEditPost = (updatedPost: BlogPost) => {
    // Update state
    const updatedPosts = posts.map(p => p.id === updatedPost.id ? updatedPost : p);
    setPosts(updatedPosts);
    
    // Update local storage
    const seedIds = initialBlogPosts.map(p => p.id);
    const customPosts = updatedPosts.filter(p => !seedIds.includes(p.id));
    localStorage.setItem('customBlogPosts', JSON.stringify(customPosts));
  };

  const handleDeletePost = (id: string) => {
      // Allow deleting any post from the view
      const updatedPosts = posts.filter(p => p.id !== id);
      setPosts(updatedPosts);

      // Update local storage
      const seedIds = initialBlogPosts.map(p => p.id);
      const customPosts = updatedPosts.filter(p => !seedIds.includes(p.id));
      localStorage.setItem('customBlogPosts', JSON.stringify(customPosts));
  };

  const getSEOMetadata = () => {
    switch (currentView) {
      case 'home':
        return {
          title: "White Label Supplements No Minimum | BuildMySupplement",
          description: "Launch your brand fast with our GMP-certified white label supplements. Zero minimums. Custom formulas built for influencers & creators. Start today!"
        };
      case 'blog':
        return {
          title: "Supplement Brand Guides & Insights | BuildMySupplement",
          description: "Learn how to build, scale, and market your supplement brand. Expert insights on manufacturing, D2C marketing, formulations, and industry trends."
        };
      case 'post':
        return {
          title: activePost?.title ? `${activePost.title} | Blog` : "Blog Post | BuildMySupplement",
          description: activePost?.excerpt || "Read our latest insights on supplement manufacturing and brand building."
        };
      case 'contact':
        return {
          title: "Contact Supplement Manufacturing Experts | BuildMySupplement",
          description: "Get a free consultation for your supplement brand. We offer custom formulation, low MOQs, and end-to-end third party manufacturing support globally."
        };
      case 'products':
        return {
          title: "Private Label Supplement Products & Formulas | BuildMySupplement",
          description: "Explore our range of ready-to-label supplements. High-quality protein, vitamins, and wellness products available for fast market entry with low MOQs."
        };
      case 'resources':
        return {
          title: "Supplement Brand Resources & FAQs | BuildMySupplement",
          description: "Everything you need to know about starting a supplement brand in India. Manufacturing FAQs, compliance guides, formulation types, and business resources."
        };
      case 'privacy':
        return {
          title: "Privacy Policy & Terms | BuildMySupplement",
          description: "Privacy policy and data protection guidelines for BuildMySupplement manufacturing and business services."
        };
      case 'white-label':
        return {
          title: "White Label Supplements No Minimum | BuildMySupplement",
          description: "Speed and simplicity. Choose from 100+ ready formulas, add your custom label, and start selling your white label supplements fast. No minimum order quantities for small brands."
        };
      case 'private-label':
        return {
          title: "Private Label Supplements | Custom Formulations BuildMySupplement",
          description: "Build a truly unique brand with custom formulations. As your private label supplement manufacturer, we offer low MOQ custom manufacturing to create your competitive edge."
        };
      case 'influencers':
        return {
          title: "Start a Supplement Brand for Influencers | BuildMySupplement",
          description: "You have the audience. Let us handle the manufacturing. Build your own supplement brand as an influencer with our premium white label solutions."
        };
      case 'about':
        return {
          title: "About Us | BuildMySupplement Manufacturer",
          description: "Learn about our mission to democratize supplement manufacturing for creators. We are a GMP-certified, FDA-registered partner for your wellness brand."
        };
      case 'certifications':
        return {
          title: "Our Certifications & Quality | BuildMySupplement",
          description: "We are a GMP-certified supplement manufacturer committed to uncompromising quality, FDA compliance, and third-party testing for your peace of mind."
        };
      default:
         return {
          title: "White Label Supplements No Minimum | BuildMySupplement",
          description: "Speed and simplicity. Choose from 100+ ready formulas, add your custom label, and start selling your white label supplements fast. No minimum order quantities."
        };
    }
  };

  const seo = getSEOMetadata();

  return (
    <div className="min-h-screen bg-white">
      <SEO title={seo.title} description={seo.description} />
      {/* Hide standard navbar on Admin pages for cleaner look, or keep it. Keeping it for consistency but simpler. */}
      {currentView !== 'admin-login' && currentView !== 'admin-dashboard' && (
        <Navbar currentPage={currentView as any} onNavigate={handleNavigate} />
      )}
      
      {/* For Admin views, show a simplified nav or back button is handled inside dashboard */}
       {(currentView === 'admin-login' || currentView === 'admin-dashboard') && (
         <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 h-20 flex items-center px-8 justify-between">
            <div className="flex items-center cursor-pointer" onClick={() => handleNavigate('home')}>
                <span className="text-3xl font-extrabold tracking-tight text-black">
                BuildMy<span className="text-orange-500">Supplement</span>
                </span>
            </div>
            <button onClick={() => handleNavigate('home')} className="text-sm font-bold text-gray-500 hover:text-black">Exit Admin</button>
         </nav>
       )}

      <main className={currentView === 'admin-dashboard' ? '' : 'pt-20'}> 
        
        {currentView === 'home' && (
          <>
            <div className="-mt-20"><Hero onNavigate={handleNavigate} /></div> 
            <Story />
            <Services onNavigate={handleNavigate} />
            <Products onNavigate={handleNavigate} />
            <Comparison />
            <Process />
            <Mission />
            <Testimonials />
            <BlogPreview posts={posts} onNavigate={handleNavigate} onReadPost={handleReadPost} />
            <CTA onNavigate={handleNavigate} />
          </>
        )}

        {currentView === 'products' && (
            <ProductsPage onNavigate={handleNavigate} />
        )}

        {currentView === 'blog' && (
          <BlogList posts={posts} onReadPost={handleReadPost} />
        )}

        {currentView === 'post' && activePost && (
          <BlogPostView post={activePost} onBack={handleBackToBlog} />
        )}

        {currentView === 'contact' && (
          <ContactPage />
        )}

        {currentView === 'resources' && (
          <ResourcesPage />
        )}

        {currentView === 'privacy' && (
          <PrivacyPolicy />
        )}

        {currentView === 'white-label' && (
          <WhiteLabelSupplements onNavigate={handleNavigate} />
        )}

        {currentView === 'private-label' && (
          <PrivateLabelSupplements onNavigate={handleNavigate} />
        )}

        {currentView === 'influencers' && (
          <SupplementsForInfluencers onNavigate={handleNavigate} />
        )}

        {currentView === 'about' && (
          <AboutUs onNavigate={handleNavigate} />
        )}

        {currentView === 'certifications' && (
          <Certifications onNavigate={handleNavigate} />
        )}

        {currentView === 'admin-login' && (
            <AdminLogin onLogin={handleAdminLoginSuccess} onCancel={() => handleNavigate('home')} />
        )}

        {currentView === 'admin-dashboard' && (
            <AdminDashboard 
                posts={posts} 
                onAddPost={handleAddPost} 
                onEditPost={handleEditPost}
                onDeletePost={handleDeletePost}
                onLogout={() => handleNavigate('home')}
            />
        )}
      </main>
      
      {currentView !== 'admin-login' && currentView !== 'admin-dashboard' && (
        <Footer onNavigate={handleNavigate} />
      )}

      {/* WhatsApp Widget - Visible on all client facing pages */}
      {currentView !== 'admin-login' && currentView !== 'admin-dashboard' && (
        <WhatsAppWidget />
      )}
    </div>
  );
}

export default App;